
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { isAdmin as checkIsAdmin } from "@/services/authService";
import { BlogPost, deleteBlogPost, useBlogPosts } from "@/hooks/useBlogPosts";
import AdminLayout from "@/components/admin/AdminLayout";
import DeletePostDialog from "@/components/admin/DeletePostDialog";
import BlogPostsHeader from "@/components/admin/BlogPostsHeader";
import BlogPostTable from "@/components/admin/BlogPostTable";

const BlogManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { posts, isLoading, error } = useBlogPosts({ published: "all" });

  // Check if the user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) {
        toast({
          title: "Access denied",
          description: "Please login to manage blog posts",
          variant: "destructive",
        });
        
        navigate("/admin/login");
      }
    };
    
    checkAuth();
  }, [navigate, toast]);

  // Filter posts based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(
        (post) =>
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.slug.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPosts(filtered);
    }
  }, [searchTerm, posts]);

  const confirmDeletePost = (id: string) => {
    setDeleteId(id);
  };

  const handleDeletePost = async () => {
    if (!deleteId) return;
    
    setIsDeleting(true);
    
    try {
      await deleteBlogPost(deleteId);
      
      toast({
        title: "Post deleted",
        description: "The blog post has been deleted successfully.",
      });
      
      // Update filtered posts list
      setFilteredPosts(prevPosts => prevPosts.filter(post => post.id !== deleteId));
      
      // Close dialog
      setDeleteId(null);
    } catch (error: any) {
      toast({
        title: "Error deleting post",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout 
      title="Blog Management"
      backLink="/admin/dashboard"
      backLabel="Back to Dashboard"
    >
      <BlogPostsHeader 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <Card>
        <CardHeader>
          <CardTitle>All Blog Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <p>Loading blog posts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>Error loading blog posts. Please try again.</p>
            </div>
          ) : (
            <BlogPostTable 
              posts={filteredPosts}
              onDeleteClick={confirmDeletePost}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeletePostDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        onDelete={handleDeletePost}
        isPending={isDeleting}
      />
    </AdminLayout>
  );
};

export default BlogManagement;
